/**
 * Jubly SalesDrive — Sales Reports
 * Pipeline funnel, win/loss rate, revenue by month, deal velocity
 */

import { useState, useEffect } from 'react';
import {
  TrendingUp, Trophy, XCircle, Briefcase, Clock,
  DollarSign, Target, BarChart2, AlertCircle, Loader2
} from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────────────────

function formatCurrency(cents) {
  if (!cents && cents !== 0) return '$0';
  const dollars = cents / 100;
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`;
  if (dollars >= 1_000)     return `$${(dollars / 1_000).toFixed(1)}K`;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(dollars);
}

// ─── Metric Card ───────────────────────────────────────────────────────────────────────────

function MetricCard({ label, value, sub, icon: Icon, iconBg, iconColor, subColor = 'text-gray-500' }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900 leading-none">{value}</p>
          {sub && <p className={`text-xs mt-1.5 font-medium ${subColor}`}>{sub}</p>}
        </div>
        <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0 ml-3`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
      </div>
    </div>
  );
}

// ─── Simple Bar Chart ───────────────────────────────────────────────────────────────────────

function BarChart({ data, valueKey, labelKey, colorFn, formatValue, height = 160 }) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-300">
        <p className="text-sm">No data available</p>
      </div>
    );
  }

  const maxVal = Math.max(...data.map(d => d[valueKey] || 0), 1);

  return (
    <div className="flex items-end gap-2" style={{ height }}>
      {data.map((d, i) => {
        const val = d[valueKey] || 0;
        const pct = maxVal > 0 ? (val / maxVal) * 100 : 0;
        const color = colorFn ? colorFn(d, i) : 'bg-indigo-500';
        return (
          <div key={i} className="flex flex-col items-center flex-1 min-w-0 h-full justify-end gap-1">
            <span className="text-[10px] text-gray-500 font-medium">
              {formatValue ? formatValue(val) : val}
            </span>
            <div
              className={`w-full rounded-t-md ${color} transition-all duration-500`}
              style={{ height: `${Math.max(pct, 2)}%` }}
              title={`${d[labelKey]}: ${formatValue ? formatValue(val) : val}`}
            />
            <span className="text-[10px] text-gray-400 truncate w-full text-center">
              {d[labelKey]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Grouped Bar Chart (won vs lost) ──────────────────────────────────────────────────

function WinLossChart({ data, height = 160 }) {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-300">
        <p className="text-sm">No closed deals in the last 6 months</p>
      </div>
    );
  }

  const maxVal = Math.max(...data.flatMap(d => [d.won || 0, d.lost || 0]), 1);

  return (
    <div>
      <div className="flex items-end gap-3" style={{ height }}>
        {data.map((d, i) => (
          <div key={i} className="flex flex-col items-center flex-1 min-w-0 h-full justify-end gap-1">
            <div className="flex items-end gap-0.5 w-full justify-center" style={{ height: '85%' }}>
              {/* Won bar */}
              <div
                className="flex-1 bg-green-500 rounded-t-sm transition-all duration-500"
                style={{ height: `${Math.max((d.won / maxVal) * 100, d.won > 0 ? 4 : 0)}%` }}
                title={`Won: ${d.won}`}
              />
              {/* Lost bar */}
              <div
                className="flex-1 bg-red-400 rounded-t-sm transition-all duration-500"
                style={{ height: `${Math.max((d.lost / maxVal) * 100, d.lost > 0 ? 4 : 0)}%` }}
                title={`Lost: ${d.lost}`}
              />
            </div>
            <span className="text-[10px] text-gray-400 truncate w-full text-center">{d.month}</span>
          </div>
        ))}
      </div>
      {/* Legend */}
      <div className="flex items-center gap-4 mt-3 justify-center">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-green-500" />
          <span className="text-xs text-gray-500">Won</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-red-400" />
          <span className="text-xs text-gray-500">Lost</span>
        </div>
      </div>
    </div>
  );
}

// ─── Funnel Chart ─────────────────────────────────────────────────────────────────────────────

function FunnelChart({ stages }) {
  if (!stages || stages.length === 0) {
    return (
      <div className="flex items-center justify-center h-32 text-gray-300">
        <p className="text-sm">No pipeline data</p>
      </div>
    );
  }

  const maxCount = Math.max(...stages.map(s => s.deal_count || 0), 1);

  return (
    <div className="space-y-2">
      {stages.map((stage, i) => {
        const pct = maxCount > 0 ? ((stage.deal_count || 0) / maxCount) * 100 : 0;
        return (
          <div key={i} className="flex items-center gap-3">
            <div className="w-24 text-xs text-gray-600 font-medium truncate flex-shrink-0 text-right">
              {stage.stage_name}
            </div>
            <div className="flex-1 bg-gray-100 rounded-full h-6 overflow-hidden">
              <div
                className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-500"
                style={{
                  width: `${Math.max(pct, stage.deal_count > 0 ? 8 : 0)}%`,
                  backgroundColor: stage.stage_color || '#6366F1',
                }}
              >
                {stage.deal_count > 0 && (
                  <span className="text-[10px] font-bold text-white">{stage.deal_count}</span>
                )}
              </div>
            </div>
            <div className="w-20 text-xs text-gray-500 flex-shrink-0">
              {formatCurrency(stage.total_value)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Export ──────────────────────────────────────────────────────────────────────────────

export default function SalesReports() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/api/crm/reports', { credentials: 'include' })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(d => { setData(d); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center px-6">
        <AlertCircle className="w-10 h-10 text-red-400 mb-3" />
        <p className="text-sm font-medium text-gray-700">Failed to load reports</p>
        <p className="text-xs text-gray-400 mt-1">{error}</p>
      </div>
    );
  }

  const { funnel = [], winLoss = [], revenue = [], velocity = {} } = data || {};

  const winRate = velocity.win_rate || 0;
  const avgDays = parseFloat(velocity.avg_days_to_close || 0).toFixed(1);
  const totalWon  = velocity.total_won  || 0;
  const totalLost = velocity.total_lost || 0;
  const totalOpen = velocity.total_open || 0;

  return (
    <div className="flex flex-col h-full overflow-y-auto bg-gray-50/40">
      <div className="max-w-5xl w-full mx-auto px-6 py-6 space-y-6">

        {/* Header */}
        <div>
          <h1 className="text-xl font-bold text-gray-900">Sales Reports</h1>
          <p className="text-sm text-gray-500 mt-0.5">Pipeline performance and deal analytics</p>
        </div>

        {/* Velocity Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            label="Win Rate"
            value={`${winRate}%`}
            sub={`${totalWon} won · ${totalLost} lost`}
            icon={Target}
            iconBg="bg-green-50"
            iconColor="text-green-600"
            subColor={winRate >= 50 ? 'text-green-600' : 'text-amber-600'}
          />
          <MetricCard
            label="Avg Days to Close"
            value={avgDays}
            sub="Won deals"
            icon={Clock}
            iconBg="bg-blue-50"
            iconColor="text-blue-600"
          />
          <MetricCard
            label="Won Revenue"
            value={formatCurrency(velocity.total_won_value || 0)}
            sub="All time"
            icon={DollarSign}
            iconBg="bg-indigo-50"
            iconColor="text-indigo-600"
            subColor="text-indigo-600"
          />
          <MetricCard
            label="Open Pipeline"
            value={formatCurrency(velocity.total_pipeline_value || 0)}
            sub={`${totalOpen} open deals`}
            icon={Briefcase}
            iconBg="bg-amber-50"
            iconColor="text-amber-600"
          />
        </div>

        {/* Pipeline Funnel */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <BarChart2 className="w-4 h-4 text-indigo-500" />
            <h2 className="text-sm font-semibold text-gray-700">Pipeline Funnel</h2>
            <span className="text-xs text-gray-400 ml-auto">Open deals per stage</span>
          </div>
          <FunnelChart stages={funnel} />
        </div>

        {/* Win/Loss + Revenue side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Win/Loss by Month */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-4 h-4 text-green-500" />
              <h2 className="text-sm font-semibold text-gray-700">Win / Loss by Month</h2>
              <span className="text-xs text-gray-400 ml-auto">Last 6 months</span>
            </div>
            <WinLossChart data={winLoss} height={160} />
          </div>

          {/* Revenue by Month */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-4 h-4 text-indigo-500" />
              <h2 className="text-sm font-semibold text-gray-700">Revenue by Month</h2>
              <span className="text-xs text-gray-400 ml-auto">Won deals</span>
            </div>
            <BarChart
              data={revenue}
              valueKey="won_value"
              labelKey="month"
              colorFn={() => 'bg-indigo-500'}
              formatValue={v => formatCurrency(v)}
              height={160}
            />
          </div>
        </div>

        {/* Deal counts by month */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Briefcase className="w-4 h-4 text-gray-400" />
            <h2 className="text-sm font-semibold text-gray-700">Deals Created by Month</h2>
            <span className="text-xs text-gray-400 ml-auto">Last 6 months</span>
          </div>
          <BarChart
            data={revenue}
            valueKey="deal_count"
            labelKey="month"
            colorFn={(d, i) => {
              const colors = ['bg-violet-400', 'bg-indigo-400', 'bg-blue-400', 'bg-sky-400', 'bg-cyan-400', 'bg-teal-400'];
              return colors[i % colors.length];
            }}
            height={140}
          />
        </div>

        {/* Win/Loss table */}
        {winLoss.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-700">Monthly Breakdown</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Month</th>
                    <th className="text-right px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Won</th>
                    <th className="text-right px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Lost</th>
                    <th className="text-right px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Win Rate</th>
                    <th className="text-right px-5 py-3 text-xs font-medium text-gray-500 uppercase tracking-wide">Revenue</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {winLoss.map((row, i) => {
                    const total = (row.won || 0) + (row.lost || 0);
                    const rate = total > 0 ? Math.round((row.won / total) * 100) : 0;
                    return (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-5 py-3 font-medium text-gray-800">{row.month}</td>
                        <td className="px-5 py-3 text-right">
                          <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                            <Trophy className="w-3 h-3" />{row.won || 0}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className="inline-flex items-center gap-1 text-red-500 font-medium">
                            <XCircle className="w-3 h-3" />{row.lost || 0}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className={`font-semibold ${rate >= 50 ? 'text-green-600' : 'text-amber-600'}`}>
                            {rate}%
                          </span>
                        </td>
                        <td className="px-5 py-3 text-right font-semibold text-indigo-600">
                          {formatCurrency(row.won_value || 0)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
